/**
 * The provider service lines in the Admin (D883, program D871 phase 5, ADR-0160) - framework-neutral view-models for the review queue per line, the "Serviços"
 * section of the provider's record and the approval of one vehicle for a line, over `provider-services-routes.ts` (D877 + D883).
 *
 * The console only FILTERS and EXPLAINS: it hides a move from whoever lacks its word (`driver:approve` reviews and approves a vehicle, `driver:suspend`
 * suspends and reinstates), offers only the moves the line's state accepts, and says every state, event and reason in words. The server is the authority on all
 * of it (the kernel's machines, the revision, the operator from the session). Neutral: no country, document, line rule or language literal lives here - the
 * lines, states and vehicle classes are platform vocabulary, the requirement codes are the market's catalog data.
 */
import type { AdminProviderServicesView, AdminProviderVehicleView, AdminServiceAuditLineView, AdminServiceEnrollmentView, AdminServiceSubmissionView, AdminVehicleLineApprovalView } from '@vello/api-client';
import { SERVICE_LINES, SERVICE_REVIEW_REJECT_REASONS, SERVICE_SUSPENSION_REASONS } from '@vello/provider-services';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type ServicesTone = 'neutral' | 'success' | 'warning' | 'danger';


/** Who may do what (display only - the server re-authorizes every call at the driver's market). */
export function providerServicesAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayReview: boolean; readonly maySuspend: boolean } {
  if (grants === undefined) return { mayView: true, mayReview: true, maySuspend: true };
  return {
    mayView: grantsAllow(grants, { permission: 'driver:view', target: 'GEO' }),
    mayReview: grantsAllow(grants, { permission: 'driver:approve', target: 'GEO' }),
    maySuspend: grantsAllow(grants, { permission: 'driver:suspend', target: 'GEO' }),
  };
}

/** The state filter of the queue: the lines waiting for a decision (the server's default), or one state. */
export const QUEUE_STATE_FILTERS = ['WAITING', 'SUBMITTED', 'IN_REVIEW', 'APPROVED', 'REJECTED', 'SUSPENDED', 'EXPIRED', 'WITHDRAWN', 'DRAFT'] as const;
export type QueueStateFilter = (typeof QUEUE_STATE_FILTERS)[number];
/** The states the queue asks for a filter (`undefined` = the server's default, SUBMITTED + IN_REVIEW). */
export const queueStates = (filter: QueueStateFilter): readonly string[] | undefined => (filter === 'WAITING' ? undefined : [filter]);

export const ENROLLMENT_STATE_TONE: Readonly<Record<string, ServicesTone>> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  IN_REVIEW: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  SUSPENDED: 'danger',
  EXPIRED: 'danger',
  WITHDRAWN: 'neutral',
};
export const VEHICLE_APPROVAL_TONE: Readonly<Record<string, ServicesTone>> = { APPROVED: 'success', SUSPENDED: 'danger', EXPIRED: 'danger' };
export const enrollmentTone = (state: string): ServicesTone => ENROLLMENT_STATE_TONE[state] ?? 'neutral';
export const vehicleApprovalTone = (state: string | undefined): ServicesTone => (state === undefined ? 'neutral' : (VEHICLE_APPROVAL_TONE[state] ?? 'neutral'));

/**
 * The review move the line's state offers: ONE primary (start the review of a submitted line, approve one under review) and, under review, the secondary
 * "do not approve" (it asks a reason). Nothing while the line is not waiting for the team.
 */
export function reviewMoves(state: string): { readonly primary?: 'START_REVIEW' | 'APPROVE'; readonly secondary?: 'REJECT' } {
  if (state === 'SUBMITTED') return { primary: 'START_REVIEW' };
  if (state === 'IN_REVIEW') return { primary: 'APPROVE', secondary: 'REJECT' };
  return {};
}

/** Suspend an approved line, reinstate a suspended one (the kernel's machine); nothing otherwise. */
export function lineSuspensionMove(state: string): 'SUSPEND' | 'REINSTATE' | undefined {
  return state === 'APPROVED' ? 'SUSPEND' : state === 'SUSPENDED' ? 'REINSTATE' : undefined;
}

/** What the Admin may do for one vehicle in one line. */
export type VehicleLineMove = 'SUSPEND' | 'REINSTATE' | 'APPROVE';
/** Why the "approve for this service" move is not offered for a vehicle with no approval yet. */
export type VehicleApprovalBlock = 'ENROLLMENT_NOT_APPROVED' | 'VEHICLE_CLASS_NOT_ALLOWED_FOR_LINE';

/** One vehicle of the provider as one line sees it: the facts, its approval for the line (if any) and the move the Admin offers. */
export interface VehicleLineRow {
  readonly vehicleId: string;
  readonly facts?: AdminProviderVehicleView;
  /** The class the line judges it by: the latest passed inspection's, else the class the server reads. */
  readonly vehicleClass?: string;
  readonly approval?: AdminVehicleLineApprovalView;
  readonly move?: VehicleLineMove;
  readonly blocked?: VehicleApprovalBlock;
  /** The vehicle the provider is on duty with, in this line's mode. */
  readonly onDutyHere: boolean;
}

/** One line of the provider: its enrollment, the rule the market publishes for it, each vehicle's row, what the last SUBMIT named. */
export interface ProviderLineView {
  readonly line: string;
  readonly enrollment: AdminServiceEnrollmentView;
  readonly rule?: { readonly enabled: boolean; readonly vehicleClasses: readonly string[] };
  readonly vehicles: readonly VehicleLineRow[];
  readonly submission?: AdminServiceSubmissionView;
}

/** The lines of the 360 read in the platform's order of lines, each with its vehicles. */
export function providerLines(view: AdminProviderServicesView): readonly ProviderLineView[] {
  const order = (line: string): number => {
    const i = (SERVICE_LINES as readonly string[]).indexOf(line);
    return i < 0 ? SERVICE_LINES.length : i;
  };
  const details = view.vehicleDetails ?? [];
  const ids = [...new Set([...details.map((d) => d.vehicleId), ...view.vehicles.map((v) => v.vehicleId), ...view.vehicleApprovals.map((a) => a.vehicleId)])];
  return [...view.enrollments]
    .sort((a, b) => order(a.line) - order(b.line) || a.line.localeCompare(b.line))
    .map((enrollment) => {
      const rule = view.rules.find((r) => r.line === enrollment.line);
      const vehicles: VehicleLineRow[] = ids.map((vehicleId) => {
        const facts = details.find((d) => d.vehicleId === vehicleId);
        const approval = view.vehicleApprovals.find((a) => a.vehicleId === vehicleId && a.line === enrollment.line);
        const vehicleClass = facts?.latestPassedInspection?.vehicleClass ?? approval?.vehicleClass ?? facts?.inspectionExempt?.vehicleClass ?? view.vehicles.find((v) => v.vehicleId === vehicleId)?.vehicleClass;
        const onDutyHere = view.session?.dutyVehicleId === vehicleId && view.session.dutyServiceMode === enrollment.line;
        const base = { vehicleId, ...(facts !== undefined ? { facts } : {}), ...(vehicleClass !== undefined ? { vehicleClass } : {}), onDutyHere };
        if (approval !== undefined) {
          const move = approval.state === 'APPROVED' ? 'SUSPEND' : approval.state === 'SUSPENDED' ? 'REINSTATE' : undefined;
          return { ...base, approval, ...(move !== undefined ? { move } : {}) };
        }
        // A vehicle that is not in service (inactive) is shown, but only a registered one (active or awaiting inspection) can be approved.
        if (!view.vehicles.some((v) => v.vehicleId === vehicleId)) return base;
        // The class the approval would record (the server's `approvalBasis`): the latest passed inspection's, else (D886) a class the catalog exempts from the
        // inspection as declared, else (D901: no inspection is needed) the class on the registered vehicle. Only the line's rule may refuse it.
        const judgedClass = facts?.latestPassedInspection?.vehicleClass ?? facts?.inspectionExempt?.vehicleClass ?? view.vehicles.find((v) => v.vehicleId === vehicleId)?.vehicleClass;
        const blocked: VehicleApprovalBlock | undefined =
          enrollment.state !== 'APPROVED'
            ? 'ENROLLMENT_NOT_APPROVED'
            : judgedClass === undefined || rule === undefined || !rule.enabled || !rule.vehicleClasses.includes(judgedClass)
              ? 'VEHICLE_CLASS_NOT_ALLOWED_FOR_LINE'
              : undefined;
        return blocked === undefined ? { ...base, move: 'APPROVE' as const } : { ...base, blocked };
      });
      const submission = view.submissions.find((s) => s.line === enrollment.line);
      return { line: enrollment.line, enrollment, ...(rule !== undefined ? { rule: { enabled: rule.enabled, vehicleClasses: rule.vehicleClasses } } : {}), vehicles, ...(submission !== undefined ? { submission } : {}) };
    });
}

/** One entry of the merged history of the two audits, newest first. */
export interface ServiceHistoryEntry {
  readonly key: string;
  readonly kind: 'ENROLLMENT' | 'VEHICLE';
  readonly line: string;
  readonly event: string;
  readonly toState: string;
  readonly source: string;
  readonly actorId?: string;
  readonly vehicleId?: string;
  /** The operator's reason code, when the move carried one. */
  readonly operatorReasonCode?: string;
  /** D898: the reason an automatic move (source `SYSTEM`) recorded, e.g. the line back with the renewed document. */
  readonly systemReasonCode?: string;
  readonly actedAt: string;
}

export function serviceHistory(view: AdminProviderServicesView, limit = 50): readonly ServiceHistoryEntry[] {
  const reasonOf = (a: AdminServiceAuditLineView): string | undefined => (typeof a.facts?.operatorReasonCode === 'string' ? a.facts.operatorReasonCode : undefined);
  const entry = (kind: 'ENROLLMENT' | 'VEHICLE') => (a: AdminServiceAuditLineView): ServiceHistoryEntry => {
    const reason = reasonOf(a);
    return {
      key: `${kind}:${a.eventId}`,
      kind,
      line: a.line,
      event: a.event,
      toState: a.toState,
      source: a.source,
      ...(a.actorId !== undefined ? { actorId: a.actorId } : {}),
      ...(a.vehicleId !== undefined ? { vehicleId: a.vehicleId } : {}),
      ...(reason !== undefined ? { operatorReasonCode: reason } : {}),
      ...(a.source === 'SYSTEM' ? { systemReasonCode: a.reasonCode } : {}),
      actedAt: a.actedAt,
    };
  };
  return [...view.enrollmentEvents.map(entry('ENROLLMENT')), ...view.vehicleApprovalEvents.map(entry('VEHICLE'))]
    .sort((a, b) => b.actedAt.localeCompare(a.actedAt) || b.key.localeCompare(a.key))
    .slice(0, limit);
}

/** A vehicle as a person names it: "Make Model · PLATE" (the parts it has), never its id. */
export function vehicleLabel(facts: AdminProviderVehicleView | undefined): string | undefined {
  if (facts === undefined) return undefined;
  const name = [facts.make, facts.model].filter((x): x is string => x !== undefined && x.trim() !== '').join(' ');
  const parts = [name, facts.plate].filter((x) => x.trim() !== '');
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/** The reason chips of a move: REJECT and SUSPEND ask one (REJECT must have it), the others none. */
export function reasonsFor(event: string): readonly string[] {
  if (event === 'REJECT') return SERVICE_REVIEW_REJECT_REASONS;
  if (event === 'SUSPEND') return SERVICE_SUSPENSION_REASONS;
  return [];
}
